import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { CATEGORIES, METHOD_LABELS } from './assetConstants';
import { FileUploadField } from '../FileUploadField';

const EMPTY = {
  name: '', category: 'equipment', purchase_date: new Date().toISOString().slice(0, 10), cost: '',
  salvage_value: '0', useful_life_years: '5', method: 'straight_line', serial_number: '', location: '', supplier: '', notes: '', attachments: [],
};
const FIELDS = ['name', 'category', 'purchase_date', 'cost', 'salvage_value', 'useful_life_years', 'method', 'serial_number', 'location', 'supplier', 'notes', 'attachments'];

const Field = ({ label, id, form, set, ...props }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input value={form[id]} onChange={(e) => set(id, e.target.value)} data-testid={`asset-field-${id}`} {...props} />
  </div>
);

const Pick = ({ label, id, form, set, options }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Select value={form[id]} onValueChange={(v) => set(id, v)}>
      <SelectTrigger data-testid={`asset-field-${id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const AssetFormDialog = ({ asset, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = asset?.asset_id;

  useEffect(() => {
    if (asset) {
      if (editing) {
        const formData = Object.fromEntries(FIELDS.map((k) => [k, k === 'attachments' ? (asset[k] || []) : String(asset[k] ?? '')]));
        setForm(formData);
      } else {
        setForm(EMPTY);
      }
    }
  }, [asset, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error('Asset name required');
    if (!(Number(form.cost) > 0)) return toast.error('Enter a valid cost');
    setSaving(true);
    try {
      const payload = {
        ...form, cost: Number(form.cost), salvage_value: Number(form.salvage_value || 0),
        useful_life_years: Number(form.useful_life_years),
        attachments: (form.attachments || []).map(a => typeof a === 'string' ? a : a.file_id),
      };
      if (editing) await api.put(`/assets/${asset.asset_id}`, payload);
      else await api.post('/assets', payload);
      toast.success(editing ? 'Asset updated' : 'Asset added');
      onClose(); onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    } finally { setSaving(false); }
  };

  const p = { form, set };
  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="asset-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit Asset' : 'Add Asset'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Asset name *" id="name" placeholder="e.g. Vaccine fridge — Haier" {...p} />
          <div className="grid grid-cols-2 gap-3">
            <Pick label="Category" id="category" options={CATEGORIES.map((c) => [c.id, c.label])} {...p} />
            <Field label="Purchase date" id="purchase_date" type="date" {...p} />
            <Field label="Cost *" id="cost" type="number" min="0" step="0.01" {...p} />
            <Field label="Salvage value" id="salvage_value" type="number" min="0" step="0.01" {...p} />
            <Field label="Useful life (years)" id="useful_life_years" type="number" min="1" max="100" {...p} />
            <Pick label="Depreciation method" id="method" options={Object.entries(METHOD_LABELS)} {...p} />
            <Field label="Serial / tag #" id="serial_number" {...p} />
            <Field label="Location" id="location" placeholder="e.g. Dispensary" {...p} />
          </div>
          <Field label="Supplier" id="supplier" {...p} />
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} className="h-16 text-xs resize-none" data-testid="asset-field-notes" />
          </div>
          <FileUploadField
            files={form.attachments || []}
            onChange={(files) => set('attachments', files)}
            category="asset_document"
            entityType="asset"
            entityId={editing || undefined}
            max={5}
            testId="asset-attachment"
            label="Documents & receipts"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="asset-save-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{editing ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
